<script>
  /**
   * @fileoverview MapLegend component provides interactive controls for filtering
   * map markers by activity event types. Users can toggle visibility of different
   * email activities (sent, opened, clicked, bounced, unsubscribed) and see
   * VPN/proxied locations.
   *
   * @component MapLegend
   * @description Interactive legend for audience insights map showing activity types
   * with color coding. Each activity type can be toggled on/off to filter map markers.
   * Includes keyboard accessibility and visual feedback for enabled/disabled states.
   *
   * @prop {string[]} mapFilters - Array of enabled activity event types
   *
   * @example
   * <MapLegend
   *   bind:mapFilters={['sent', 'opened']}
   * />
   */

  import { format } from 'svelte-i18n';

  import { ACTIVITY_EVENTS } from '@client/enums';

  /**
   * Array of currently enabled activity event types for map filtering.
   * Each string should match a key from ACTIVITY_EVENTS enum.
   * When empty, all activity types appear disabled in the legend.
   * @type {string[]}
   */
  export let mapFilters = [];

  /**
   * Toggles the visibility of a specific activity event type on the map.
   * Adds the type to mapFilters if not present, removes it if already present.
   * Updates the mapFilters array to trigger reactivity in parent components.
   * @param {string} type - Activity event type key (e.g., 'sent', 'opened', 'clicked')
   */
  const toggleEvent = (type) => {
    if (mapFilters.includes(type)) {
      mapFilters.splice(mapFilters.indexOf(type), 1);
    } else {
      mapFilters.push(type);
    }

    // Trigger reactivity by creating new array reference
    mapFilters = [...mapFilters];
  };
</script>

<div class="legend">
  {#each Object.entries(ACTIVITY_EVENTS) as [metric]}
    <div
      class="item"
      role="button"
      tabindex="0"
      class:disabled={!mapFilters.includes(metric)}
      on:click={() => toggleEvent(metric)}
    >
      <div class="dot" style="background-color: {ACTIVITY_EVENTS[metric].color}" />
      <div>{$format(ACTIVITY_EVENTS[metric].label)}</div>
    </div>
  {/each}
  <div class="item proxy">
    <div class="dot" />
    <div>{$format('label.VPN_PROXIED_LOCATION')}</div>
  </div>
</div>

<style lang="scss" src="./map-legend.scss"></style>
